import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AdminNavigation } from "../components/navigation/AdminNavigation";
import { toast } from "sonner";
import {
  ArrowLeft,
  Search,
  RefreshCw,
  Undo2,
  Wallet as WalletIcon,
  Ban,
  Ticket,
  Package,
  Loader2,
  ShieldCheck,
  ReceiptText,
} from "lucide-react";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin Refund Console — searchable table of every historical purchase.
// One-click "Refund to Wallet" fires a triple-write:
//   1. Provider refund via the Universal Payment Adapter
//   2. payment_transactions.payment_status = "refunded" — deducts pending
//      earnings from the artist automatically (earnings query filters on
//      payment_status == "paid")
//   3. Signed ledger row credits the fan's wallet with the refunded amount
//   plus revoke of any seller_orders + tickets tied to the transaction.
//
// PIN gate: the same PAYOUT_APPROVAL_PIN used by the payouts console. One
// code for ops to remember; both flows move real money.
const AdminRefunds = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("paid");

  const fetchRows = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ status, limit: "50", offset: "0" });
      if (q.trim()) params.set("q", q.trim());
      const headers = { Authorization: `Bearer ${token}` };
      // Card (Stripe) transactions + Token (wallet) orders, rendered in ONE table.
      const [card, wallet] = await Promise.all([
        axios.get(`${API}/admin/refunds/orders?${params.toString()}`, { headers }),
        axios.get(`${API}/admin/wallet/orders?${params.toString()}`, { headers }).catch(() => ({ data: { rows: [], total: 0 } })),
      ]);
      const merged = [
        ...(wallet.data.rows || []).map((r) => ({ ...r, provider: "wallet" })),
        ...(card.data.rows || []).map((r) => ({ ...r, provider: r.provider || "card" })),
      ].sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
      setRows(merged);
      setTotal((card.data.total || 0) + (wallet.data.total || 0));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [token, q, status]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const refund = async (row) => {
    const isWallet = row.provider === "wallet";
    const amountStr = isWallet ? `${(row.amount_cents / 100).toFixed(2)} Tokens` : `$${(row.amount_cents / 100).toFixed(2)}`;
    const confirm = window.confirm(
      `Refund ${amountStr} to ${row.buyer_email || row.buyer_id}?\n\n` +
      `This will:\n` +
      (isWallet
        ? `  • Return ${amountStr} (incl. tax) to the fan's StardomFans Wallet.\n`
        : `  • Refund the money at the payment provider.\n  • Credit ${amountStr} into the fan's StardomFans Wallet.\n`) +
      `  • Revoke their access to "${row.item_name || row.item_type}".\n` +
      `  • Deduct the sale from the artist's pending earnings` + (isWallet ? ` and reverse the tax-vault liability.\n\n` : `.\n\n`) +
      `You'll be asked for the treasury PIN to confirm.`
    );
    if (!confirm) return;

    const pin = window.prompt("Enter PAYOUT_APPROVAL_PIN to authorize the refund:", "");
    if (!pin) return;

    setBusyId(row.transaction_id);
    try {
      const url = isWallet
        ? `${API}/admin/wallet/orders/${row.order_id}/refund`
        : `${API}/admin/refunds/${row.transaction_id}`;
      const { data } = await axios.post(
        url,
        { memo: "Refunded via admin refund console" },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Payout-Approval-Pin": pin,
          },
        }
      );
      toast.success(
        `Refunded ${amountStr} via ${data.provider}. Wallet credited, ` +
        `${data.orders_refunded} order(s) revoked, ${data.tickets_refunded} ticket(s) voided.`,
        { duration: 6000 }
      );
      fetchRows();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Refund failed"));
    } finally {
      setBusyId(null);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-black text-white pb-24" data-testid="admin-refunds-page">
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur border-b border-[#1A2030]">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-lg border border-[#1A2030] hover:bg-[#1A2030] flex items-center justify-center"
            data-testid="refunds-back-btn"
            aria-label="Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Link to="/home" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
            <span className="hidden sm:inline text-white font-bold">StardomFans · Admin</span>
          </Link>
          <div className="ml-auto flex items-center gap-2">
            <Link
              to="/admin/payouts"
              className="hidden sm:inline-flex items-center gap-2 h-9 px-3 rounded-full border border-[#1A2030] text-[#8A92A0] hover:text-white hover:border-[#EC008C]/40 text-xs"
              data-testid="link-to-payouts"
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              Payouts
            </Link>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-widest text-[#8A92A0]">Treasury</p>
              <p className="text-white font-bold">Refund Console</p>
            </div>
          </div>
        </div>
      </header>
      <AdminNavigation />

      <main className="max-w-6xl mx-auto px-4 py-8 sm:py-12 space-y-6">
        {/* Header + search */}
        <section className="rounded-xl border border-[#1A2030] bg-[#0A0E18] p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#EC008C]/15 border border-[#EC008C]/30 flex-shrink-0">
              <ReceiptText className="w-4 h-4 text-[#EC008C]" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">Buyer refunds</p>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">Refund any past order</h1>
              <p className="text-xs text-[#8A92A0] mt-1 max-w-2xl">
                Search by fan email or Order ID. One click refunds the buyer&apos;s card, credits their Wallet
                with the same amount, revokes their access to the purchase, and pulls the sale out of the
                artist&apos;s pending earnings so payouts stay accurate.
              </p>
              <p className="text-[11px] text-[#F0C464] mt-2 max-w-2xl">
                <Ban className="w-3 h-3 inline-block mr-1 -mt-0.5" />
                Digital products (downloads, albums, livestreams, remix challenges) are
                <strong className="text-white"> non-refundable</strong> — once the file / stream / entry ships,
                we can&apos;t claw it back. Tickets, experiences, and physical merch stay fully refundable.
              </p>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3">
            <div className="relative">
              <Search className="w-4 h-4 text-[#8A92A0] absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") fetchRows(); }}
                placeholder="Search by fan email, Order ID, txn id, session id…"
                className="w-full h-11 pl-10 pr-4 rounded-lg bg-black border border-[#1A2030] text-white text-sm placeholder:text-[#4A5060] focus:border-[#EC008C] focus:outline-none"
                data-testid="refunds-search-input"
              />
            </div>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="h-11 px-3 rounded-lg bg-black border border-[#1A2030] text-white text-sm focus:border-[#EC008C] focus:outline-none"
              data-testid="refunds-status-filter"
            >
              <option value="paid">Refundable (paid)</option>
              <option value="refunded">Already refunded</option>
              <option value="all">All</option>
            </select>
            <button
              onClick={fetchRows}
              className="h-11 px-4 rounded-lg bg-[#EC008C] text-black font-bold text-sm inline-flex items-center gap-2 hover:bg-[#FF3399]"
              data-testid="refunds-search-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Search
            </button>
          </div>
        </section>

        {/* Results */}
        <section className="rounded-xl border border-[#1A2030] bg-[#0A0E18] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#1A2030] flex items-center justify-between">
            <p className="text-xs text-[#8A92A0]">
              {loading ? "Loading…" : `${rows.length} of ${total} order${total === 1 ? "" : "s"} shown`}
            </p>
          </div>

          {loading ? (
            <div className="p-10 flex items-center justify-center text-[#8A92A0] gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading orders…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-[#8A92A0] text-sm" data-testid="refunds-empty-state">
              No orders match. Try a different email or ID.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="refunds-table">
                <thead className="text-[10px] uppercase tracking-widest text-[#8A92A0]">
                  <tr className="border-b border-[#1A2030]">
                    <th className="text-left px-4 py-3 font-medium">Date</th>
                    <th className="text-left px-4 py-3 font-medium">Buyer</th>
                    <th className="text-left px-4 py-3 font-medium">Item</th>
                    <th className="text-left px-4 py-3 font-medium">Artist</th>
                    <th className="text-right px-4 py-3 font-medium">Amount</th>
                    <th className="text-left px-4 py-3 font-medium">Status</th>
                    <th className="text-right px-4 py-3 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.transaction_id}
                      className="border-b border-[#1A2030] hover:bg-[#0F1421]"
                      data-testid={`refund-row-${row.transaction_id}`}
                    >
                      <td className="px-4 py-3 text-xs text-[#8A92A0] whitespace-nowrap">
                        {row.created_at ? row.created_at.slice(0, 10) : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-white text-xs" data-testid={`refund-email-${row.transaction_id}`}>
                          {row.buyer_email || "—"}
                        </p>
                        <p className="text-[10px] text-[#4A5060] font-mono">
                          {row.order_id || row.transaction_id}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {row.item_type === "ticket" ? (
                            <Ticket className="w-3.5 h-3.5 text-[#8DB87A]" />
                          ) : (
                            <Package className="w-3.5 h-3.5 text-[#F0C464]" />
                          )}
                          <div>
                            <p className="text-white text-xs">{row.item_name || row.item_type}</p>
                            <p className="text-[10px] text-[#4A5060] uppercase tracking-wide">
                              {row.item_type}{row.quantity > 1 ? ` × ${row.quantity}` : ""}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#8A92A0]">
                        {row.artist_name || row.artist_id?.slice(0, 12) || "—"}
                      </td>
                      <td className="px-4 py-3 text-right text-white font-bold">
                        {row.amount_display}
                        {row.provider === "wallet" && (
                          <span className="block text-[9px] uppercase tracking-widest text-[#EC008C] font-semibold" data-testid={`refund-provider-${row.transaction_id}`}>
                            Tokens
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {row.payment_status === "refunded" ? (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#EC008C] bg-[#EC008C]/10 border border-[#EC008C]/30 rounded-full px-2 py-0.5">
                            <Ban className="w-3 h-3" />
                            Refunded
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#8DB87A] bg-[#8DB87A]/10 border border-[#8DB87A]/30 rounded-full px-2 py-0.5">
                            Paid
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {row.refundable ? (
                          <button
                            onClick={() => refund(row)}
                            disabled={busyId === row.transaction_id}
                            className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-[#EC008C] text-black text-xs font-bold hover:bg-[#FF3399] disabled:opacity-60"
                            data-testid={`refund-btn-${row.transaction_id}`}
                          >
                            {busyId === row.transaction_id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <>
                                <Undo2 className="w-3 h-3" />
                                <WalletIcon className="w-3 h-3" />
                              </>
                            )}
                            Refund to Wallet
                          </button>
                        ) : row.refund_block_reason && row.payment_status !== "refunded" ? (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#8A92A0] bg-[#1A2030] border border-[#1A2030] rounded-full px-2 py-0.5"
                            title={row.refund_block_reason}
                            data-testid={`refund-blocked-${row.transaction_id}`}
                          >
                            <Ban className="w-3 h-3" />
                            {row.refund_block_reason.includes("Cart") ? "Cart has digital" : "Non-refundable"}
                          </span>
                        ) : row.refunded_at ? (
                          <p className="text-[10px] text-[#4A5060]">
                            {row.refunded_at.slice(0, 10)}<br />
                            by {row.refunded_by_admin?.split("@")[0] || "admin"}
                          </p>
                        ) : (
                          <span className="text-[10px] text-[#4A5060]">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default AdminRefunds;
